/**
 * DE-9510 — proactive bearer renew and 401 refresh decision helpers.
 * Pure functions so Jest can cover policy without booting axios/MSAL.
 */

import {
  isTokenExpiringSoon,
  TOKEN_RENEW_BEFORE_EXPIRY_MS,
  tryParseJwt,
} from "./token";

/** Window flag: Cypress may opt into silent renew for targeted E2E (DE-9510). */
export const ADS_ALLOW_TOKEN_RENEW_IN_CYPRESS_FLAG =
  "__ADS_ALLOW_TOKEN_RENEW_IN_CYPRESS__";

export const getAllowTokenRenewInCypress = () => {
  try {
    if (typeof window === "undefined") return false;
    // Fixed flag name — not user-controlled dynamic indexing.
    // eslint-disable-next-line security/detect-object-injection
    return Boolean(window[ADS_ALLOW_TOKEN_RENEW_IN_CYPRESS_FLAG]);
  } catch (_e) {
    return false;
  }
};

/**
 * Cypress normally skips MSAL silent acquire. Opt in only when the AUT sets
 * `window.__ADS_ALLOW_TOKEN_RENEW_IN_CYPRESS__` for DE-9510 renew E2E.
 */
export const shouldSkipSilentAcquire = (isCypressRuntime) =>
  Boolean(isCypressRuntime) && !getAllowTokenRenewInCypress();

/**
 * Renew before send when MSAL is ready, silent acquire is allowed, and the
 * bearer expires within the 5-minute window.
 */
export const shouldProactivelyRenewBearer = ({
  bearer,
  msalInitialized,
  skipSilentAcquire,
  nowMs = Date.now(),
  thresholdMs = TOKEN_RENEW_BEFORE_EXPIRY_MS,
}) => {
  if (!bearer || !msalInitialized || skipSilentAcquire) {
    return false;
  }
  return isTokenExpiringSoon(bearer, thresholdMs, nowMs);
};

export const isIdempotentMethod = (method) => {
  if (!method) return false;
  const m = String(method).toLowerCase();
  return (
    m === "get" ||
    m === "head" ||
    m === "options" ||
    m === "put" ||
    m === "delete"
  );
};

export const isBearerExpired = (
  authorizationHeader,
  nowMs = Date.now(),
) => {
  if (!authorizationHeader) return false;
  const match = String(authorizationHeader).match(/bearer\s+(.+)/i);
  const token = match && match[1] ? match[1] : null;
  if (!token) return false;
  const payload = tryParseJwt(token);
  if (!payload || !payload.exp) return false;
  return payload.exp * 1000 <= nowMs;
};

export const serverSaysInvalidToken = (headers) => {
  if (!headers) return false;
  const hdr = headers["www-authenticate"] || headers["WWW-Authenticate"];
  if (!hdr) return false;
  return /invalid_token|expired/i.test(String(hdr));
};

/**
 * Narrow 401 refresh+retry gate (same rules as the axios response interceptor).
 */
export const shouldRetry401WithTokenRefresh = ({
  httpStatus,
  method,
  hadBearer,
  authorizationHeader,
  responseHeaders,
  alreadyRetried,
  isLoggingOut,
  skipSilentAcquire,
  nowMs = Date.now(),
}) => {
  if (httpStatus !== 401) return false;
  if (skipSilentAcquire || isLoggingOut || alreadyRetried) return false;
  if (!hadBearer || !isIdempotentMethod(method)) return false;
  const expired = isBearerExpired(authorizationHeader, nowMs);
  const invalidByServer = serverSaysInvalidToken(responseHeaders);
  return expired || invalidByServer;
};
